<!--
  DatesFilterWithCustomRange renders a filter with dropdown date options and reads
  from URL query params `start_date` and `end_date`, and emits to parent component
  when the date range is changed
-->

<script>
import { iFilter } from '@inscribe/signal';
import { formatAsDateString, subDays, differenceInDays } from '@/utils/date';
import moment from 'moment';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

import { ref } from 'vue';

const myRange = ref([]);

const OPTION = {
  PAST_DAY: 'Past day',
  PAST_7_DAYS: 'Past 7 days',
  PAST_30_DAYS: 'Past 30 days',
  PAST_90_DAYS: 'Past 90 days',
  CUSTOM_RANGE: 'Custom range',
};

export default {
  components: {
    iFilter,
    VueDatePicker,
  },
  props: {
    params: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      myRange,
      attrs: {
        key: 'today',
        dot: true,
        highlight: 'red',

        dates: new Date(),
      },
    };
  },
  computed: {
    value() {
      const startDate = this.params.start_date || this.params.startDate;
      const endDate = this.params.end_date || this.params.endDate;
      if (!startDate || !endDate) {
        return [];
      }

      const startDateObj = new Date(startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (differenceInDays(startDateObj, today) <= 1) {
        return [OPTION.PAST_DAY];
      }

      const daysFromStartToToday = differenceInDays(startDateObj, today);

      if (daysFromStartToToday === 7) {
        return [OPTION.PAST_7_DAYS];
      } else if (daysFromStartToToday === 30) {
        return [OPTION.PAST_30_DAYS];
      } else if (daysFromStartToToday === 90) {
        return [OPTION.PAST_90_DAYS];
      }
      return [OPTION.CUSTOM_RANGE];
    },
    options() {
      return Object.values(OPTION).map((o) =>
        o === OPTION.CUSTOM_RANGE ? { label: o, range: true } : { label: o },
      );
    },
    customRangeLabel() {
      return this.myRange?.length
        ? `${moment(this.myRange[0]).format('DD/MM/YYYY')} - ${moment(
            this.myRange[1],
          ).format('DD/MM/YYYY')}`
        : '';
    },
    rangeString() {
      if (!this.rangeSet) {
        return 'Select date range';
      } else {
        return this.customRangeLabel;
      }
    },
    rangeSet() {
      const startM = moment(this.myRange[0]);
      const startE = moment(this.myRange[1]);
      return !startM.isSame(startE, 'day');
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue.includes(OPTION.CUSTOM_RANGE)) {
          this.syncRangeValues();
        }
      },
    },
  },
  methods: {
    onSelectionChanged(updated) {
      let startDate = null;
      let endDate = null;
      const today = new Date();
      const todayStr = formatAsDateString(today);

      const updatedOption = updated.selection[0]?.label;
      if (updatedOption === OPTION.PAST_DAY) {
        startDate = formatAsDateString(subDays(today, 1));
        endDate = todayStr;
      } else if (updatedOption === OPTION.PAST_7_DAYS) {
        startDate = formatAsDateString(subDays(today, 7));
        endDate = todayStr;
      } else if (updatedOption === OPTION.PAST_30_DAYS) {
        startDate = formatAsDateString(subDays(today, 30));
        endDate = todayStr;
      } else if (updatedOption === OPTION.PAST_90_DAYS) {
        startDate = formatAsDateString(subDays(today, 90));
        endDate = todayStr;
      } else if (updatedOption === OPTION.CUSTOM_RANGE) {
        if (this.myRange?.length === 2) {
          startDate = formatAsDateString(this.myRange[0]);
          endDate = formatAsDateString(this.myRange[1]);
        }
      }

      this.$emit('dates-filter-updated', {
        startDate,
        endDate,
      });
    },
    syncRangeValues() {
      this.myRange = [
        moment(this.params.start_date || this.params.startDate),
        moment(this.params.end_date || this.params.endDate),
      ];
    },
    format(dates) {
      let formatDate = `${formatAsDateString(new Date(dates[0]))}`;
      if (dates[1]) {
        formatDate += ` to ${formatAsDateString(new Date(dates[1]))}`;
      }
      return formatDate;
    },
  },
};
</script>

<template>
  <iFilter
    label="Dates"
    :options="options"
    :searchable="false"
    :max-selectable="1"
    :model-value="value"
    :custom-range="true"
    :custom-range-label="customRangeLabel"
    class="dates-filter"
    @selection-changed="onSelectionChanged"
  >
    <template #custom-range>
      <VueDatePicker
        v-model="myRange"
        :max-date="new Date()"
        :format="format"
        range
        :time-picker="false"
        :month-change-on-scroll="false"
      ></VueDatePicker>
    </template>
  </iFilter>
</template>

<style lang="scss" scoped>
:deep(.dp__theme_light) {
  --dp-primary-color: #39075b;
  --dp-primary-text-color: white;
  --dp-secondary-text-color: #39075b;
  --dp-border-color: #5f0f98;
  --dp-hover-text-color: #5f0f98;
  --dp-hover-color: #f8f0fe;
  --dp-range-between-dates-background-color: #f8f0fe;
  --dp-range-between-dates-text-color: #5f0f98;
  --dp-range-between-border-color: var(--dp-hover-color, #f3f3f3);
  --dp-menu-border-color: #eee;
}

:deep(.dp--tp-wrap) {
  display: none;
}

:deep(.dp__input) {
  font-size: 12px;
}
</style>
